import { CheckCircle2 } from "lucide-react";
import SEO from "@/components/SEO";

const packages = [
  {
    tier: "01",
    name: "Essentials",
    tagline: "Focus Verticale — 1 Pilastro",
    audience: "Progettato per le imprese che devono risolvere un collo di bottiglia specifico e urgente.",
    intro: "Se c'è un'area aziendale che in questo momento sta bloccando la tua crescita o sta assorbendo tutto il tuo tempo, questo percorso isola il problema e lo azzera. Scegliamo un singolo pilastro del Metodo A.L.L.I.N.E.A. per una diagnosi e una ristrutturazione profonda.",
    focus: "Mappatura dei processi correnti, diagnosi operativa approfondita, individuazione delle dispersioni e sblocco immediato dell'area critica.",
    duration: "3 mesi di affiancamento intensivo, con monte ore di consulenza diretta e call operative strategiche, strutturato per darti la massima flessibilità.",
    deliverable: "Il rilascio delle Linee Guida Strategiche personalizzate per l'implementazione autonoma e il mantenimento dell'efficienza raggiunta.",
    featured: false,
    variant: "clay",
    badge: null,
  },
  {
    tier: "02",
    name: "Core",
    tagline: "Reingegnerizzazione Strutturale — 3 Pilastri",
    audience: "Progettato per le aziende che vogliono riorganizzare i flussi interni e consolidare la presenza sul mercato.",
    intro: "Un intervento integrato che connette l'efficienza dei flussi di lavoro aziendali con il posizionamento del brand. Selezioniamo i 3 pilastri più critici per la tua crescita attuale — ad esempio Assetto Operativo, Leadership e Identità — per creare una struttura fluida e misurabile.",
    focus: "Ottimizzazione, reingegnerizzazione e standardizzazione dei flussi di lavoro. L'analisi preliminare d'impresa viene eseguita in un'unica sessione integrata per massimizzare l'efficienza dei tempi.",
    duration: "6 mesi di affiancamento continuativo con supporto mensile programmato per guidare te e il tuo team nel cambio di passo operativo.",
    deliverable: "Sviluppo e consegna delle SOP (Procedure Operative Standardizzate) per le aree d'intervento, affiancate da strumenti di reportistica e monitoraggio dei KPI aziendali per il controllo autonomo delle performance.",
    featured: true,
    variant: "plain",
    badge: "Il più richiesto",
  },
  {
    tier: "03",
    name: "Executive",
    tagline: "Direzione e Trasformazione Radicale — 7 Pilastri",
    audience: "Progettato per gli imprenditori che vogliono liberare il proprio tempo e posizionare l'azienda al vertice del mercato.",
    intro: "La massima espressione del metodo. Un affiancamento a 360 gradi in cui entro fisicamente nella governance operativa della tua azienda come vero e proprio braccio destro strategico. Coordiniamo in modo indissolubile la ristrutturazione interna e il lancio della Brand Identity sul mercato esterno.",
    focus: "Intervento totale e sinergico su tutti i 7 Pilastri del Metodo A.L.L.I.N.E.A. Riorganizzazione dei flussi, gestione e motivazione delle risorse umane, ingegneria del posizionamento di marca (online e offline), audit continui e sblocco sistemico del tempo del titolare.",
    duration: "12 mesi di affiancamento totale e costante. Prevede la mia presenza fisica in azienda per il coordinamento diretto di risorse, flussi e strategie.",
    deliverable: "Direzione strategica permanente, ristrutturazione completa degli asset organizzativi, automazione dei processi e costruzione di un'azienda autonoma capace di produrre risultati anche senza la presenza fisica del fondatore.",
    featured: true,
    variant: "dark",
    badge: "Pochi posti disponibili",
  },
];

const CAL = "https://calendar.app.google/wMsDARnKAyyi4doX6";

export default function Servizi() {
  return (
    <>
      <SEO
        path="/percorsi"
        title="I Miei Percorsi — Consulenza per Aziende e PMI (Essentials, Core, Executive)"
        description="Tre formule di affiancamento consulenziale con il Metodo A.L.L.I.N.E.A.: Essentials (3 mesi, 1 pilastro), Core (6 mesi, 3 pilastri), Executive (12 mesi, 7 pilastri). Percorsi sartoriali per imprese italiane."
      />
      {/* HERO */}
      <section className="section-pad">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-4xl" data-reveal>
            <p className="eyebrow mb-4">I Percorsi di Affiancamento</p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.05] text-[#2A3229]">
              Nessun pacchetto pronto.<br/>Solo percorsi di trasformazione <span className="text-[#323C36]">su misura.</span>
            </h1>
            <div className="mt-10 space-y-6 text-lg text-[#5A6058] leading-relaxed max-w-3xl">
              <p>La tua azienda non ha bisogno di altra teoria, ma di un assetto millimetrico. I miei percorsi di affiancamento non si basano su consulenze standardizzate, ma sull'applicazione sartoriale del <strong className="text-[#2A3229]">Metodo A.L.L.I.N.E.A.</strong> all'interno della tua specifica realtà aziendale.</p>
              <p>Intervengo direttamente al fianco del titolare e del team per sbloccare i flussi operativi e blindare l'identità di marca, strutturando l'impresa in base al suo reale livello di maturità e ai suoi obiettivi di espansione.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 3 LIVELLI */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="mb-16 max-w-3xl" data-reveal>
            <p className="eyebrow mb-3">I 3 Livelli</p>
            <h2 className="font-display text-3xl sm:text-4xl text-[#2A3229] leading-tight">
              Tre formule di affiancamento, un unico obiettivo: <span className="text-[#A8674F]">portare ordine e crescita strutturata</span>.
            </h2>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {packages.map((p, i) => {
              const isDark = p.variant === "dark";
              const isClay = p.variant === "clay";
              const inverted = isDark || isClay; // light text on colored bg
              const cardBg = isDark
                ? "bg-[#2A3229] text-[#F6F3EF]"
                : isClay
                  ? "bg-[#A8674F] text-[#F6F3EF]"
                  : "bg-white border border-[#323C36]/12 lg:-mt-4";
              const badgeBg = isClay ? "bg-[#FAC075] text-[#2A3229]" : "bg-[#FAC075] text-[#2A3229]";
              const accentText = isDark ? "text-[#FAC075]" : isClay ? "text-[#FAC075]" : "text-[#A8674F]";
              const titleText = inverted ? "text-[#F6F3EF]" : "text-[#2A3229]";
              const softText = inverted ? "text-[#F6F3EF]/80" : "text-[#5A6058]";
              const italicText = inverted ? "text-[#F6F3EF]/80" : "text-[#2A3229]";
              const dividerBorder = inverted ? "border-[#F6F3EF]/15" : "border-[#323C36]/15";
              const eyebrowClass = inverted ? "!text-[#FAC075]" : "";
              const bodyText = inverted ? "text-[#F6F3EF]/80" : "text-[#2A3229]";
              const btnClass = isDark
                ? "bg-[#FAC075] text-[#2A3229] hover:bg-[#E8A050]"
                : isClay
                  ? "bg-[#2A3229] text-[#F6F3EF] hover:bg-[#1F2822]"
                  : "border border-[#323C36] text-[#323C36] hover:bg-[#323C36] hover:text-[#F6F3EF]";

              return (
              <article
                key={i}
                data-testid={`package-${p.name.toLowerCase()}`}
                data-reveal
                data-reveal-delay={`${i + 1}`}
                className={`relative flex flex-col p-10 sm:p-12 transition-all duration-500 hover:-translate-y-1 ${cardBg}`}
              >
                {p.badge && (
                  <span className={`absolute -top-3 left-10 ${badgeBg} text-[10px] tracking-[0.28em] uppercase font-semibold px-3 py-1.5`}>
                    {p.badge}
                  </span>
                )}

                <p className={`text-xs tracking-[0.28em] uppercase font-semibold ${accentText}`}>
                  Livello {p.tier}
                </p>
                <h3 className={`font-display text-4xl sm:text-5xl mt-3 tracking-tight ${titleText}`}>
                  {p.name}
                </h3>
                <p className={`mt-2 text-sm font-medium ${softText}`}>
                  {p.tagline}
                </p>

                <p className={`mt-8 italic text-sm leading-relaxed ${italicText}`}>
                  {p.audience}
                </p>

                <p className={`mt-6 text-sm leading-relaxed ${softText}`}>
                  {p.intro}
                </p>

                <div className={`mt-10 pt-6 border-t space-y-6 text-sm ${dividerBorder}`}>
                  <div>
                    <p className={`eyebrow !text-[10px] mb-2 ${eyebrowClass}`}>Focus Strategico</p>
                    <p className={`${bodyText} leading-relaxed`}>{p.focus}</p>
                  </div>
                  <div>
                    <p className={`eyebrow !text-[10px] mb-2 ${eyebrowClass}`}>Formula & Durata</p>
                    <p className={`${bodyText} leading-relaxed`}>{p.duration}</p>
                  </div>
                  <div>
                    <p className={`eyebrow !text-[10px] mb-2 ${eyebrowClass}`}>Deliverable</p>
                    <p className={`${bodyText} leading-relaxed`}>{p.deliverable}</p>
                  </div>
                </div>

                <a
                  data-testid={`package-cta-${p.name.toLowerCase()}`}
                  href={CAL}
                  target="_blank"
                  rel="noreferrer"
                  className={`mt-10 inline-flex items-center justify-center gap-2 px-6 py-3.5 text-[11px] uppercase tracking-[0.18em] font-semibold transition-all ${btnClass}`}
                >
                  Candidati al percorso →
                </a>
              </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA FINALE */}
      <section className="section-pad">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <p className="eyebrow mb-4">Sessione Strategica</p>
          <h2 className="font-display text-4xl sm:text-5xl text-[#2A3229] leading-tight">
            Pronto a portare la tua azienda al <span className="text-[#323C36]">prossimo livello di maturità</span>?
          </h2>
          <p className="mt-8 text-lg text-[#5A6058] max-w-2xl mx-auto leading-relaxed">
            I posti per gli affiancamenti diretti — in particolare per la formula Executive — sono limitati mensilmente per garantire la massima focalizzazione strategica su ogni singola impresa.
          </p>
          <div className="mt-12 flex flex-wrap justify-center gap-4">
            <a data-testid="servizi-cta-book" href={CAL} target="_blank" rel="noreferrer" className="btn-primary">
              Prenota la tua sessione strategica →
            </a>
            <a data-testid="servizi-cta-quiz" href="/checkup" className="btn-ghost-accent">
              Inizia dal Check‑Up
            </a>
          </div>
          <p className="mt-8 text-xs text-[#5A6058] flex items-center justify-center gap-2">
            <CheckCircle2 size={14} className="text-[#323C36]"/> Sessione conoscitiva su Meet · 30 minuti
          </p>
        </div>
      </section>
    </>
  );
}
